/**
 * 새 기기에서 기존 데이터 가져오기 / 충돌 해결.
 */
import { SCHEMA_VERSION, type AnyEntity } from '@/domain/schema/v1';
import { isFrozen } from '@/domain/rules/writeRules';
import type { DataKey } from '../crypto/envelope';
import { isVaultHeader, unlockWithPassphrase, type VaultHeader } from '../crypto/vault';
import { META, type CounselDb } from '../db/schema';
import type { Repository } from '../repo/repository';
import { VAULT_FILE, VaultMismatchError } from './engine';
import type { RemoteStore } from './remote';

export class NoRemoteVaultError extends Error {
  constructor() {
    super('이 Google 계정의 Drive에 CounselAI 데이터가 없습니다.');
    this.name = 'NoRemoteVaultError';
  }
}

export async function readRemoteVault(remote: RemoteStore): Promise<VaultHeader | null> {
  const raw = await remote.read(VAULT_FILE);
  if (!raw) return null;
  const h = JSON.parse(raw) as unknown;
  if (!isVaultHeader(h)) throw new VaultMismatchError();
  return h;
}

/**
 * 빈 기기에 Drive의 vault를 연결한다 (passphrase 확인 후 헤더만 저장).
 * 실제 기록은 이어지는 동기화가 가져온다. 이미 vault가 있는 기기에서는 호출하지 않는다.
 */
export async function joinRemoteVault(
  db: CounselDb,
  remote: RemoteStore,
  passphrase: string,
): Promise<{ header: VaultHeader; dataKey: DataKey }> {
  if (await db.meta.get(META.vault)) throw new Error('이 기기에는 이미 vault가 있습니다');
  const header = await readRemoteVault(remote);
  if (!header) throw new NoRemoteVaultError();
  const dataKey = await unlockWithPassphrase(header, passphrase); // 틀리면 WrongSecretError, 아무것도 저장하지 않음
  await db.transaction('rw', db.meta, async () => {
    if (await db.meta.get(META.vault)) throw new Error('이 기기에는 이미 vault가 있습니다');
    await db.meta.put({ key: META.vault, value: header });
    await db.meta.put({ key: META.dataSchemaVersion, value: SCHEMA_VERSION });
  });
  return { header, dataKey };
}

const META_KEYS = [
  'id',
  'entityType',
  'schemaVersion',
  'revision',
  'createdAt',
  'createdBy',
  'updatedAt',
  'updatedBy',
  'lastModifiedDevice',
];
const contentOf = (e: AnyEntity) =>
  Object.fromEntries(Object.entries(e as Record<string, unknown>).filter(([k]) => !META_KEYS.includes(k)));

export class ConflictChoiceError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ConflictChoiceError';
  }
}

/**
 * 충돌 해결: 사용자가 고른 쪽 내용으로 새 버전을 만든다 (양쪽 revision보다 큰 번호).
 * 상대 버전의 지문을 조상으로 기록해, 다른 기기들이 이 결과를 "최신"으로 받아들이게 한다.
 * 승인된 기록이 걸린 충돌은 승인된 쪽만 고를 수 있다 (승인 기록 무단 변경 금지).
 */
export async function resolveConflict(
  repo: Repository,
  db: CounselDb,
  conflictId: string,
  choice: 'local' | 'remote',
): Promise<void> {
  const c = await repo.get('SyncConflict', conflictId);
  if (c.resolution !== 'unresolved') return;
  const local = (await repo.get(c.targetEntityType, c.targetEntityId, { includeDeleted: true })) as AnyEntity;
  const remote = c.remotePayload as AnyEntity;
  if (choice === 'remote' && isFrozen(local))
    throw new ConflictChoiceError('이 기기의 기록은 승인되어 잠겨 있습니다. 승인된 쪽을 유지하세요.');
  if (choice === 'local' && isFrozen(remote) && !isFrozen(local))
    throw new ConflictChoiceError('다른 기기에서 승인된 기록입니다. 승인된 쪽을 유지하세요.');

  const content = choice === 'local' ? contentOf(local) : contentOf(remote);
  if (choice === 'remote' || !isFrozen(local)) {
    await repo.commitVersion(local, content, Math.max(local.revision, c.remoteRevision) + 1, 'user');
  }
  if (c.remoteHash) {
    await db.ancestry.put({
      entityType: c.targetEntityType,
      id: c.targetEntityId,
      hash: c.remoteHash,
      revision: c.remoteRevision,
    });
  }
  // 새 버전을 만들지 않은 경우(승인본 유지)에도 다른 기기가 이 해결을 받도록 해당 기록을 다시 올린다
  const cur = await db.entities.get([c.targetEntityType, c.targetEntityId]);
  if (cur)
    await db.outbox.add({
      entityType: cur.entityType,
      id: cur.id,
      revision: cur.revision,
      queuedAt: new Date().toISOString(),
    });
  await repo.update('SyncConflict', conflictId, { resolution: choice, resolvedAt: new Date().toISOString() }, 'user');
}
